import type { ReactElement } from "react";

import { utcDateOnly } from "../../lib/date";
import type { TrendPoint } from "../../api/types";

// A small SVG line/scatter of FaceAge over uploads, with chronological age as a
// faint reference where a date of birth is on file. Points are spaced evenly by
// submission order; the x-axis is labelled with each submission's date.

// Date only, from the UTC calendar date: `captured_at` is the participant's
// chosen photo date stored as UTC midnight, so local-time getters would shift
// it a day back (and show a meaningless "7:00 PM") in negative timezones.
function tickLabel(iso: string): string {
  const [y, m, d] = utcDateOnly(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
}

// Up to `max` evenly spaced indices (always including the first and last), so a
// long series doesn't crowd the axis with overlapping labels.
function tickIndices(n: number, max = 8): number[] {
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const out = new Set<number>();
  for (let k = 0; k < max; k++) out.add(Math.round((k * (n - 1)) / (max - 1)));
  return [...out];
}

// Whole-year y-axis ticks at a step (1/2/5/10/…) that keeps the count readable,
// over a domain padded one step beyond the data so lines never sit on an edge.
function yTicks(dataMin: number, dataMax: number): { lo: number; hi: number; ticks: number[] } {
  const span = Math.max(dataMax - dataMin, 1);
  const step = [1, 2, 5, 10, 20, 50].find((s) => span / s <= 5) ?? 50;
  const lo = Math.floor(dataMin / step) * step - step;
  const hi = Math.ceil(dataMax / step) * step + step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi; v += step) ticks.push(v);
  return { lo, hi, ticks };
}

export function TrendsChart({ points }: { points: TrendPoint[] }): ReactElement | null {
  const withAge = points.filter((p) => p.estimated_age != null);
  if (withAge.length === 0) return null;

  const W = 640;
  const H = 250;
  const padL = 56; // room for the y-axis tick labels + the rotated axis title
  const padR = 20;
  const padT = 16;
  const padB = 56; // room for the rotated date/time labels

  const ages = withAge.map((p) => p.estimated_age);
  const chrono = withAge.map((p) => p.chronological_age).filter((v): v is number => v != null);
  const { lo, hi, ticks } = yTicks(Math.min(...ages, ...chrono), Math.max(...ages, ...chrono));

  const x = (i: number): number =>
    withAge.length === 1 ? (padL + W - padR) / 2 : padL + (i * (W - padL - padR)) / (withAge.length - 1);
  const y = (v: number): number => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const baseY = H - padB;
  const midY = (padT + baseY) / 2;

  const linePath = withAge
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.estimated_age).toFixed(1)}`)
    .join(" ");

  // Chronological age as a dashed reference line through the points that have it.
  const chronoPath = withAge
    .map((p, i) => (p.chronological_age != null ? { i, v: p.chronological_age } : null))
    .filter((pt): pt is { i: number; v: number } => pt !== null)
    .map((pt, k) => `${k === 0 ? "M" : "L"} ${x(pt.i).toFixed(1)} ${y(pt.v).toFixed(1)}`)
    .join(" ");

  return (
    <div className="trends-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="FaceAge over time">
        {/* y-axis: gridlines + age labels + title, so the scale is readable */}
        {ticks.map((v) => (
          <g key={`y-${v}`}>
            <line
              x1={padL}
              y1={y(v)}
              x2={W - padR}
              y2={y(v)}
              stroke="var(--border)"
              strokeWidth={1}
              opacity={0.5}
            />
            <text
              x={padL - 8}
              y={y(v) + 3}
              fontSize={10}
              fill="var(--text-secondary)"
              textAnchor="end"
            >
              {v}
            </text>
          </g>
        ))}
        <text
          x={14}
          y={midY}
          fontSize={10}
          fill="var(--text-secondary)"
          textAnchor="middle"
          transform={`rotate(-90 14 ${midY})`}
        >
          Age (years)
        </text>

        {/* axis lines */}
        <line x1={padL} y1={padT} x2={padL} y2={baseY} stroke="var(--border)" strokeWidth={1} />
        <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="var(--border)" strokeWidth={1} />

        {chronoPath ? (
          <path
            d={chronoPath}
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth={1.5}
            strokeDasharray="5 5"
          />
        ) : null}
        <path d={linePath} fill="none" stroke="var(--teal)" strokeWidth={2} />
        {withAge.map((p, i) => (
          <g key={p.photo_id}>
            {p.chronological_age != null ? (
              <circle cx={x(i)} cy={y(p.chronological_age)} r={3} fill="var(--text-muted)" />
            ) : null}
            <circle cx={x(i)} cy={y(p.estimated_age)} r={4} fill="var(--teal)" />
          </g>
        ))}

        {/* x-axis date/time labels */}
        {tickIndices(withAge.length).map((i) => {
          const pt = withAge[i];
          if (!pt) return null;
          return (
            <g key={`tick-${pt.photo_id}`}>
              <line x1={x(i)} y1={baseY} x2={x(i)} y2={baseY + 4} stroke="var(--border)" strokeWidth={1} />
              <text
                x={x(i)}
                y={baseY + 10}
                fontSize={10}
                fill="var(--text-secondary)"
                textAnchor="end"
                transform={`rotate(-35 ${x(i)} ${baseY + 8})`}
              >
                {tickLabel(pt.captured_at)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="trends-legend">
        <span>
          <i className="line teal" aria-hidden="true" /> FaceAge (from your photos)
        </span>
        {chrono.length > 0 ? (
          <span>
            <i className="line dashed" aria-hidden="true" /> Chronological age (your real age)
          </span>
        ) : null}
      </div>
    </div>
  );
}
